import { Document as DocumentT } from "@prismicio/types-internal/lib/content"
import { type ReactElement, Suspense } from "react"
import { type Location, useLocation } from "react-router-dom"
import { z } from "zod"

import { preloadRepository } from "../app/services/repository"
import { preloadProfile } from "../app/services/user"
import { Document } from "./Document"
import {
  type NewDocumentData,
  useCustomType,
  useDocumentMetaTitle
} from "./documentData"
import { DocumentProvider } from "./DocumentProvider"
import { useNewDocumentParams } from "./documentRouting"
import { DocumentSkeleton } from "./DocumentSkeleton"
import { UIDProvider } from "./UIDProvider"

export function NewDocument() {
  return (
    <Suspense fallback={<DocumentSkeleton screen="new" />}>
      <UIDProvider>
        <DataLoader>
          {(data) => (
            <DocumentProvider data={data}>
              <Document />
            </DocumentProvider>
          )}
        </DataLoader>
      </UIDProvider>
    </Suspense>
  )
}

interface DataLoaderProps {
  children: (data: NewDocumentData) => ReactElement
}

function DataLoader(props: DataLoaderProps) {
  const { children } = props

  const { customTypeId, groupLangId, locale, releaseId } =
    useNewDocumentParams()

  preloadRepository()
  preloadProfile()

  const customType = useCustomType(customTypeId)

  const location = useLocation()
  const asCopy = getValidCopyDoc(location)

  const documentData = {
    type: "new",
    groupLangId,
    locale,
    customType,
    asCopy,
    releaseId
  } as const

  useDocumentMetaTitle(documentData)

  return children(documentData)
}

function getValidCopyDoc(location: Location) {
  const locationType = z.object({
    state: z.object({
      document: z.record(z.unknown()),
      title: z.string(),
      tags: z.array(z.string())
    })
  })
  const parsed = locationType.safeParse(location)

  if (!parsed.success) return

  const document = validateDocument(parsed.data.state.document)

  return { ...parsed.data.state, document }
}

function validateDocument(maybeDoc: object) {
  const result = DocumentT.decode(maybeDoc)
  if (result._tag === "Left") {
    console.warn("could not decode document")
    return {}
  }
  return result.right
}
